import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import './BotCreate.css';

interface Credential {
  id: string;
  exchange: string;
  label: string;
}

interface BotConfig {
  sl_pct: number;
  tp_pct: number;
  leverage: number;
  size_pct: number;
}

interface VBV2Config {
  volume_spike_x: number;
  max_prior_spikes: number;
  rsi_min_momentum: number;
  rsi_extreme: number;
  atr_period: number;
  sl_atr_multiplier: number;
  tp_atr_multiplier: number;
  max_sl_pct: number;
  max_tp_pct: number;
  cooldown_minutes: number;
  confirm_timeout: number;
  min_squeeze_score: number;
  body_ratio_min: number;
  ob_imbalance_min: number;
}

interface ScanConfig {
  min_volume_24h: number;
  max_symbols: number;
  exclude: string[];
  only_quote: string;
}

const strategies = [
  {
    type: 'volume_breakout_v2',
    name: 'Volume Breakout V2',
    description: 'Squeeze Score selection + 4-phase breakout with virtual P&L tracking',
  },
  {
    type: 'volume_breakout',
    name: 'Volume Breakout (V1)',
    description: 'ATR squeeze + volume spike breakouts (legacy)',
  },
  {
    type: 'ob_imbalance',
    name: 'OB Imbalance',
    description: 'Trade order book bid/ask imbalances',
  },
  {
    type: 'momentum',
    name: 'Momentum Ignition',
    description: 'Catch momentum moves with Fibonacci retracements',
  },
  {
    type: 'funding_rate',
    name: 'Funding Rate Scalp',
    description: 'Contrarian trades before funding settlements',
  },
];

const exchanges = ['bybit', 'mexc', 'okx'];

const BotCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [strategyType, setStrategyType] = useState('');
  const [exchange, setExchange] = useState('bybit');
  const [credentialId, setCredentialId] = useState('');
  const [scanMode, setScanMode] = useState(true);
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [scanConfig, setScanConfig] = useState<ScanConfig>({
    min_volume_24h: 5000000,
    max_symbols: 15,
    exclude: [],
    only_quote: 'USDT',
  });
  const [excludeInput, setExcludeInput] = useState('');
  const [config, setConfig] = useState<BotConfig>({
    sl_pct: 0.5,
    tp_pct: 0.7,
    leverage: 5,
    size_pct: 10,
  });
  const [vbv2Config, setVbv2Config] = useState<VBV2Config>({
    volume_spike_x: 2.0,
    max_prior_spikes: 3,
    rsi_min_momentum: 50,
    rsi_extreme: 92,
    atr_period: 10,
    sl_atr_multiplier: 1.5,
    tp_atr_multiplier: 2.25,
    max_sl_pct: 0.75,
    max_tp_pct: 1.5,
    cooldown_minutes: 5,
    confirm_timeout: 3,
    min_squeeze_score: 30,
    body_ratio_min: 0.4,
    ob_imbalance_min: 0,
  });

  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await fetch(getApiUrl('/api/credentials'), {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCredentials(data);
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
    }
  };

  const filteredCredentials = credentials.filter(c => c.exchange === exchange);

  const handleNext = () => {
    if (step === 0 && !strategyType) {
      setError('Please select a strategy');
      return;
    }
    if (step === 1 && !credentialId) {
      setError('Please select credentials');
      return;
    }
    if (step === 1 && !scanMode && !symbol) {
      setError('Please enter a symbol');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleCreateBot = async () => {
    setLoading(true);
    setError('');

    try {
      const mergedConfig = strategyType === 'volume_breakout_v2'
        ? { leverage: config.leverage, size_pct: config.size_pct, ...vbv2Config }
        : config;
      const body: any = {
        credential_id: credentialId,
        strategy_type: strategyType,
        exchange,
        scan_mode: scanMode,
        config: mergedConfig,
      };
      if (scanMode) {
        body.scan_config = scanConfig;
      } else {
        body.symbol = symbol;
      }

      const response = await fetch(getApiUrl('/api/bots'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        navigate('/bots');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create bot');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="bot-create-step-indicator">
      {['Strategy', 'Exchange', 'Parameters', 'Confirm'].map((label, idx) => (
        <React.Fragment key={idx}>
          <div className={`step-circle ${idx <= step ? 'active' : ''}`}>
            {idx + 1}
          </div>
          <div className="step-label">{label}</div>
          {idx < 3 && <div className={`step-line ${idx < step ? 'active' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep0 = () => (
    <div className="bot-create-step">
      <h2 className="text-2xl font-bold text-viz-text-primary mb-6">
        {t('Choose Strategy')}
      </h2>
      <div className="strategy-grid">
        {strategies.map(s => (
          <div
            key={s.type}
            className={`strategy-card ${strategyType === s.type ? 'selected' : ''}`}
            onClick={() => setStrategyType(s.type)}
          >
            <h3 className="text-lg font-semibold text-viz-text-primary mb-2">
              {s.name}
            </h3>
            <p className="text-sm text-viz-text-secondary">{s.description}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const handleAddExclude = () => {
    const sym = excludeInput.trim().toUpperCase();
    if (sym && !scanConfig.exclude.includes(sym)) {
      setScanConfig({ ...scanConfig, exclude: [...scanConfig.exclude, sym] });
      setExcludeInput('');
    }
  };

  const handleRemoveExclude = (sym: string) => {
    setScanConfig({ ...scanConfig, exclude: scanConfig.exclude.filter(s => s !== sym) });
  };

  const formatVolume = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(0)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return v.toString();
  };

  const renderStep1 = () => (
    <div className="bot-create-step">
      <h2 className="text-2xl font-bold text-viz-text-primary mb-6">
        {t('Exchange & Symbol Mode')}
      </h2>

      <div className="mb-6">
        <label className="block text-viz-text-secondary text-sm mb-2">
          {t('Exchange')}
        </label>
        <div className="flex gap-4">
          {exchanges.map(ex => (
            <label key={ex} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="exchange"
                value={ex}
                checked={exchange === ex}
                onChange={e => {
                  setExchange(e.target.value);
                  setCredentialId('');
                }}
                className="accent-viz-accent"
              />
              <span className="text-viz-text-primary capitalize">{ex}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-viz-text-secondary text-sm mb-2">
          {t('Credentials')}
        </label>
        {filteredCredentials.length > 0 ? (
          <select
            value={credentialId}
            onChange={e => setCredentialId(e.target.value)}
            className="w-full bg-viz-bg-tertiary border border-viz-border text-viz-text-primary rounded-lg px-3 py-2 focus:border-viz-accent focus:outline-none"
          >
            <option value="">{t('Select credentials')}</option>
            {filteredCredentials.map(c => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        ) : (
          <div className="text-viz-text-secondary text-sm">
            {t('No credentials found for this exchange.')}{' '}
            <button
              onClick={() => navigate('/credentials')}
              className="text-viz-accent hover:underline"
            >
              {t('Add credentials')}
            </button>
          </div>
        )}
      </div>

      {/* Scanner / Fixed toggle */}
      <div className="mb-6 p-4 bg-viz-bg-tertiary border border-viz-border rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-viz-text-primary font-semibold">Auto Scanner</span>
            <p className="text-viz-text-secondary text-xs mt-1">
              Scan top symbols by volume, find patterns automatically
            </p>
          </div>
          <button
            onClick={() => setScanMode(!scanMode)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              scanMode ? 'bg-viz-accent' : 'bg-viz-bg-secondary'
            }`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                scanMode ? 'translate-x-6' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {scanMode ? (
          <div className="space-y-4 mt-4 pt-4 border-t border-viz-border">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-viz-text-secondary text-xs mb-1">
                  Min 24h Volume (USDT)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={500000}
                    max={50000000}
                    step={500000}
                    value={scanConfig.min_volume_24h}
                    onChange={e => setScanConfig({ ...scanConfig, min_volume_24h: parseInt(e.target.value) })}
                    className="flex-1 scan-slider"
                  />
                  <span className="text-viz-text-primary text-sm font-mono w-12 text-right">
                    {formatVolume(scanConfig.min_volume_24h)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-viz-text-secondary text-xs mb-1">
                  Max Symbols
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={3}
                    max={30}
                    step={1}
                    value={scanConfig.max_symbols}
                    onChange={e => setScanConfig({ ...scanConfig, max_symbols: parseInt(e.target.value) })}
                    className="flex-1 scan-slider"
                  />
                  <span className="text-viz-text-primary text-sm font-mono w-8 text-right">
                    {scanConfig.max_symbols}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-viz-text-secondary text-xs mb-1">
                Quote Currency
              </label>
              <div className="flex gap-2">
                {['USDT', 'USDC'].map(q => (
                  <button
                    key={q}
                    onClick={() => setScanConfig({ ...scanConfig, only_quote: q })}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      scanConfig.only_quote === q
                        ? 'bg-viz-accent text-white'
                        : 'bg-viz-bg-secondary text-viz-text-secondary hover:text-viz-text-primary'
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-viz-text-secondary text-xs mb-1">
                Exclude Symbols
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={excludeInput}
                  onChange={e => setExcludeInput(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddExclude())}
                  placeholder="e.g. BTCUSDT"
                  className="flex-1 bg-viz-bg-secondary border border-viz-border text-viz-text-primary rounded-lg px-3 py-1.5 text-sm focus:border-viz-accent focus:outline-none"
                />
                <button
                  onClick={handleAddExclude}
                  className="bg-viz-bg-secondary hover:bg-viz-border text-viz-text-secondary rounded-lg px-3 py-1.5 text-sm transition-colors"
                >
                  Add
                </button>
              </div>
              {scanConfig.exclude.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {scanConfig.exclude.map(sym => (
                    <span
                      key={sym}
                      className="inline-flex items-center gap-1 bg-viz-bg-secondary text-viz-text-secondary rounded-md px-2 py-0.5 text-xs"
                    >
                      {sym}
                      <button
                        onClick={() => handleRemoveExclude(sym)}
                        className="text-viz-text-secondary hover:text-red-400 ml-0.5"
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4 pt-4 border-t border-viz-border">
            <label className="block text-viz-text-secondary text-xs mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={e => setSymbol(e.target.value.toUpperCase())}
              placeholder="BTCUSDT"
              className="w-full bg-viz-bg-secondary border border-viz-border text-viz-text-primary rounded-lg px-3 py-2 focus:border-viz-accent focus:outline-none"
            />
          </div>
        )}
      </div>
    </div>
  );

  const sliderField = (
    label: string, value: number, min: number, max: number, step: number,
    onChange: (v: number) => void, format?: (v: number) => string
  ) => (
    <div>
      <label className="block text-viz-text-secondary text-xs mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          className="flex-1 scan-slider" />
        <span className="text-viz-text-primary text-sm font-mono w-14 text-right">
          {format ? format(value) : value}
        </span>
      </div>
    </div>
  );

  const renderVBV2Params = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-viz-text-primary text-sm font-semibold mb-3">Volume Detection</h3>
        <div className="grid grid-cols-2 gap-4">
          {sliderField('Volume Spike (RVOL)', vbv2Config.volume_spike_x, 1.5, 5, 0.1,
            v => setVbv2Config({ ...vbv2Config, volume_spike_x: v }), v => `${v.toFixed(1)}x`)}
          {sliderField('Max Prior Spikes', vbv2Config.max_prior_spikes, 0, 10, 1,
            v => setVbv2Config({ ...vbv2Config, max_prior_spikes: v }))}
        </div>
      </div>

      <div>
        <h3 className="text-viz-text-primary text-sm font-semibold mb-3">Confirmation</h3>
        <div className="grid grid-cols-2 gap-4">
          {sliderField('Body Ratio Min', vbv2Config.body_ratio_min, 0.2, 0.8, 0.05,
            v => setVbv2Config({ ...vbv2Config, body_ratio_min: v }), v => v.toFixed(2))}
          {sliderField('Confirm Candles', vbv2Config.confirm_timeout, 1, 10, 1,
            v => setVbv2Config({ ...vbv2Config, confirm_timeout: v }))}
        </div>
      </div>

      <div>
        <h3 className="text-viz-text-primary text-sm font-semibold mb-3">Momentum (RSI)</h3>
        <div className="grid grid-cols-2 gap-4">
          {sliderField('Min Momentum', vbv2Config.rsi_min_momentum, 40, 60, 1,
            v => setVbv2Config({ ...vbv2Config, rsi_min_momentum: v }))}
          {sliderField('Extreme (skip)', vbv2Config.rsi_extreme, 85, 98, 1,
            v => setVbv2Config({ ...vbv2Config, rsi_extreme: v }))}
        </div>
      </div>

      <div>
        <h3 className="text-viz-text-primary text-sm font-semibold mb-3">Risk Management</h3>
        <div className="grid grid-cols-3 gap-4">
          {sliderField('ATR Period', vbv2Config.atr_period, 7, 14, 1,
            v => setVbv2Config({ ...vbv2Config, atr_period: v }))}
          {sliderField('SL x ATR', vbv2Config.sl_atr_multiplier, 0.5, 3, 0.1,
            v => setVbv2Config({ ...vbv2Config, sl_atr_multiplier: v }), v => `${v.toFixed(1)}x`)}
          {sliderField('TP x ATR', vbv2Config.tp_atr_multiplier, 0.5, 5, 0.25,
            v => setVbv2Config({ ...vbv2Config, tp_atr_multiplier: v }), v => `${v.toFixed(2)}x`)}
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          {sliderField('Max SL %', vbv2Config.max_sl_pct, 0.1, 3, 0.05,
            v => setVbv2Config({ ...vbv2Config, max_sl_pct: v }), v => `${v.toFixed(2)}%`)}
          {sliderField('Max TP %', vbv2Config.max_tp_pct, 0.25, 5, 0.25,
            v => setVbv2Config({ ...vbv2Config, max_tp_pct: v }), v => `${v.toFixed(2)}%`)}
        </div>
      </div>

      <div>
        <h3 className="text-viz-text-primary text-sm font-semibold mb-3">Timing</h3>
        <div className="grid grid-cols-2 gap-4">
          {sliderField('Cooldown (min)', vbv2Config.cooldown_minutes, 1, 60, 1,
            v => setVbv2Config({ ...vbv2Config, cooldown_minutes: v }))}
          {sliderField('Min Squeeze Score', vbv2Config.min_squeeze_score, 0, 80, 5,
            v => setVbv2Config({ ...vbv2Config, min_squeeze_score: v }))}
        </div>
      </div>

      <div>
        <h3 className="text-viz-text-primary text-sm font-semibold mb-3">Position</h3>
        <div className="grid grid-cols-2 gap-4">
          {sliderField('Leverage', config.leverage, 1, 50, 1,
            v => setConfig({ ...config, leverage: v }), v => `${v}x`)}
          {sliderField('Size %', config.size_pct, 1, 100, 1,
            v => setConfig({ ...config, size_pct: v }), v => `${v}%`)}
        </div>
      </div>
    </div>
  );

  const renderGenericParams = () => (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <label className="block text-viz-text-secondary text-sm mb-2">{t('Stop Loss %')}</label>
        <input type="number" value={config.sl_pct}
          onChange={e => setConfig({ ...config, sl_pct: parseFloat(e.target.value) })}
          min="0.1" max="5.0" step="0.1"
          className="w-full bg-viz-bg-tertiary border border-viz-border text-viz-text-primary rounded-lg px-3 py-2 focus:border-viz-accent focus:outline-none" />
      </div>
      <div>
        <label className="block text-viz-text-secondary text-sm mb-2">{t('Take Profit %')}</label>
        <input type="number" value={config.tp_pct}
          onChange={e => setConfig({ ...config, tp_pct: parseFloat(e.target.value) })}
          min="0.1" max="10.0" step="0.1"
          className="w-full bg-viz-bg-tertiary border border-viz-border text-viz-text-primary rounded-lg px-3 py-2 focus:border-viz-accent focus:outline-none" />
      </div>
      <div>
        <label className="block text-viz-text-secondary text-sm mb-2">{t('Leverage')}</label>
        <input type="number" value={config.leverage}
          onChange={e => setConfig({ ...config, leverage: parseInt(e.target.value) })}
          min="1" max="50" step="1"
          className="w-full bg-viz-bg-tertiary border border-viz-border text-viz-text-primary rounded-lg px-3 py-2 focus:border-viz-accent focus:outline-none" />
      </div>
      <div>
        <label className="block text-viz-text-secondary text-sm mb-2">{t('Position Size %')}</label>
        <input type="number" value={config.size_pct}
          onChange={e => setConfig({ ...config, size_pct: parseInt(e.target.value) })}
          min="1" max="100" step="1"
          className="w-full bg-viz-bg-tertiary border border-viz-border text-viz-text-primary rounded-lg px-3 py-2 focus:border-viz-accent focus:outline-none" />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="bot-create-step">
      <h2 className="text-2xl font-bold text-viz-text-primary mb-6">
        {t('Parameters')}
      </h2>
      {strategyType === 'volume_breakout_v2' ? renderVBV2Params() : renderGenericParams()}
    </div>
  );

  const renderStep3 = () => {
    const selectedStrategy = strategies.find(s => s.type === strategyType);
    const selectedCredential = credentials.find(c => c.id === credentialId);

    return (
      <div className="bot-create-step">
        <h2 className="text-2xl font-bold text-viz-text-primary mb-6">
          {t('Confirm')}
        </h2>

        <div className="bg-viz-bg-tertiary border border-viz-border rounded-xl p-6 space-y-4">
          <div className="flex justify-between">
            <span className="text-viz-text-secondary">{t('Strategy')}</span>
            <span className="text-viz-text-primary font-semibold">
              {selectedStrategy?.name}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-viz-text-secondary">{t('Exchange')}</span>
            <span className="text-viz-text-primary font-semibold capitalize">
              {exchange}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-viz-text-secondary">{t('Credentials')}</span>
            <span className="text-viz-text-primary font-semibold">
              {selectedCredential?.label}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-viz-text-secondary">{t('Mode')}</span>
            <span className="text-viz-text-primary font-semibold">
              {scanMode ? `Auto Scanner (${scanConfig.max_symbols} symbols)` : symbol}
            </span>
          </div>

          <div className="border-t border-viz-border pt-4 mt-4">
            {strategyType === 'volume_breakout_v2' ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['RVOL Spike', `${vbv2Config.volume_spike_x}x`],
                  ['Max Prior Spikes', vbv2Config.max_prior_spikes],
                  ['RSI Momentum', `${vbv2Config.rsi_min_momentum}-${vbv2Config.rsi_extreme}`],
                  ['Body Ratio', vbv2Config.body_ratio_min],
                  ['SL', `${vbv2Config.sl_atr_multiplier}x ATR(${vbv2Config.atr_period}), max ${vbv2Config.max_sl_pct}%`],
                  ['TP', `${vbv2Config.tp_atr_multiplier}x ATR(${vbv2Config.atr_period}), max ${vbv2Config.max_tp_pct}%`],
                  ['Cooldown', `${vbv2Config.cooldown_minutes}m`],
                  ['Min Squeeze', vbv2Config.min_squeeze_score],
                  ['Leverage', `${config.leverage}x`],
                  ['Size', `${config.size_pct}%`],
                ].map(([label, val], i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-viz-text-secondary">{label}</span>
                    <span className="text-viz-text-primary font-mono">{val}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-viz-text-secondary text-sm">{t('SL %')}</span>
                  <span className="text-viz-text-primary">{config.sl_pct}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-viz-text-secondary text-sm">{t('TP %')}</span>
                  <span className="text-viz-text-primary">{config.tp_pct}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-viz-text-secondary text-sm">{t('Leverage')}</span>
                  <span className="text-viz-text-primary">{config.leverage}x</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-viz-text-secondary text-sm">{t('Size %')}</span>
                  <span className="text-viz-text-primary">{config.size_pct}%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 pt-20 pb-24">
      <div className="bg-viz-bg-secondary border border-viz-border rounded-xl p-8 max-w-3xl mx-auto">
        {renderStepIndicator()}

        <div className="mt-8">
          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {error && step !== 3 && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-between mt-8 pt-6 border-t border-viz-border">
          {step > 0 && (
            <button
              onClick={handleBack}
              className="bg-viz-bg-tertiary hover:bg-viz-bg-tertiary/80 text-viz-text-primary rounded-lg px-6 py-2 transition-colors"
            >
              {t('Back')}
            </button>
          )}

          <div className="ml-auto">
            {step < 3 ? (
              <button
                onClick={handleNext}
                className="bg-viz-accent hover:bg-viz-accent-hover text-white rounded-lg px-6 py-2 transition-colors"
              >
                {t('Next')}
              </button>
            ) : (
              <button
                onClick={handleCreateBot}
                disabled={loading}
                className="bg-viz-accent hover:bg-viz-accent-hover text-white rounded-lg px-6 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('Creating...') : t('Launch Bot')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotCreate;
